import "../../assets/styles/components/textEditor.css";

import React, { useCallback, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import { debounce } from "lodash";

import StarterKit from "@tiptap/starter-kit";
import { Color } from "@tiptap/extension-color";
import { Highlight } from "@tiptap/extension-highlight";
import { Placeholder } from "@tiptap/extension-placeholder";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { Underline } from "@tiptap/extension-underline";
import { TextAlign } from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import { EditorContent, useEditor } from "@tiptap/react";

import TextFormatter from "../editQuiz/TextFormatter";

const TextEditor = ({
  placeholder,
  separateMenuBar = false,
  customClass,
  noFocusStyles = false,
  width = "100%",
  height = "100%",
  onContentChange,
  setEditor,
  initialContent = "",
  isTitle = false,
  customPlaceholder = null,
  minHeight = null,
}) => {
  const tiptapContainerRef = useRef(null);

  const customSuperscript = Superscript.extend({
    addKeyboardShortcuts() {
      return {
        "Mod-Shift-ArrowUp": () => this.editor.commands.toggleSuperscript(),
      };
    },
  });
  const customSubscript = Subscript.extend({
    addKeyboardShortcuts() {
      return {
        "Mod-Shift-ArrowDown": () => this.editor.commands.toggleSubscript(),
      };
    },
  });

  const extensions = [
    StarterKit.configure({
      bulletList: { keepMarks: true, keepAttributes: false },
      orderedList: { keepMarks: true, keepAttributes: false },
    }),
    Placeholder.configure({
      placeholder: placeholder ? placeholder : "Write something here...",
    }),
    Color.configure({ types: [TextStyle.name] }),
    TextAlign.configure({ types: ["heading", "paragraph", "codeBlock"] }),
    Highlight.configure(),
    customSuperscript.configure(),
    customSubscript.configure(),
    Underline.configure(),
  ];

  const sanitizeContent = (content) => {
    return DOMPurify.sanitize(content);
  };

  const editor = useEditor({
    extensions: extensions,
    content: initialContent || "",
    editorProps: {
      attributes: {
        spellcheck: "false",
      },
    },
    onUpdate({ editor }) {
      debouncedContentChange(editor.getHTML());
    },
  });

  const debouncedContentChange = useCallback(
    debounce((content) => {
      const sanitizedContent = sanitizeContent(content);
      onContentChange(sanitizedContent);
    }, 500),
    [],
  );

  useEffect(() => {
    if (!noFocusStyles && tiptapContainerRef.current) {
      tiptapContainerRef.current.classList.add("focusStyle");
      const tiptapEditor = tiptapContainerRef.current.querySelector(".tiptap");
      if (height) {
        tiptapEditor.style.height = height;
      }
      if (minHeight) {
        tiptapEditor.style.minHeight = minHeight;
      }
    }
    const handleContainerClick = () => {
      const html = editor.getHTML();
      if (isTitle && !html.includes("<h1")) {
        const content = editor.getText();
        editor.commands.setContent(
          `<h1 style="text-align: center">${content}</h1>`,
        );
        return;
      }
    };
    tiptapContainerRef.current.addEventListener("click", handleContainerClick);
    return () => {};
  }, [noFocusStyles, height]);

  if (!editor) {
    return null;
  }

  return (
    <main style={{ width: width ? width : "100%" }}>
      <div
        ref={tiptapContainerRef}
        className={customClass}
        id="text-editor-container"
      >
        {separateMenuBar && <TextFormatter editor={editor} />}
        {!editor.getText() && customPlaceholder && (
          <div className="editor-custom-placeholder">{customPlaceholder}</div>
        )}
        <EditorContent
          onFocus={() => !separateMenuBar && setEditor(editor)}
          editor={editor}
        />
      </div>
    </main>
  );
};

export default TextEditor;
